import { useEffect, useState, useMemo } from 'react'

import { delay, map } from 'lodash'

import { library } from '@fortawesome/fontawesome-svg-core'
import { faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Trans } from '@lingui/macro'
import Item from 'Root/App/PopoverPanel/Item'
import Title from 'Root/App/Title'
import {
  CREATE_MENU_DEFAULT_OPTIONS,
  CreateMenuItem,
  CONTENT_TYPES,
  OLD_FEATURES_CONTENT_TYPES,
} from 'lib/element-types'
import getQueryString from 'lib/get-query-string'
import { translateWhenIsId } from 'lib/translate-when-is-id'
import { useFeatureEnabledByDate } from 'lib/use-feature-enabled-by-date'
import { useRouter } from 'next/router'

library.add(faChevronRight)

interface MenuCreateElementsProps {
  creatingFrom?: 'Folder' | 'Collection' | 'Content'
  onClose: Function
  onReady: Function
  moduleId?: string
}

const getCreateElementPageSearchParams = (
  identifier: string,
  path: string,
  mid?: string
) => {
  if (!identifier) return ''

  if (path.startsWith('/f')) {
    return `fid=${identifier}`
  }

  if (path.startsWith('/cc')) {
    return `ccid=${identifier}${mid ? `&mid=${mid}` : ''}`
  }

  return ''
}

const MenuCreateElements = ({
  creatingFrom,
  onClose,
  onReady,
  moduleId,
}: MenuCreateElementsProps) => {
  const { query, asPath } = useRouter()
  const [loading, featureIsEnabledForThisUser] = useFeatureEnabledByDate()
  const [currentElements, _setCurrentElements] = useState<CreateMenuItem[]>([])
  const [isRoot, setIsRoot] = useState<boolean>(true)
  const [index, setIndex] = useState(0)
  const [parentElements, setParentElements] = useState<CreateMenuItem[]>([])

  const createElementPageSearchParams = getCreateElementPageSearchParams(
    getQueryString(query.identifier),
    asPath,
    moduleId
  )

  const createMenuItems = useMemo(() => {
    if (loading) return []
    if (creatingFrom === 'Collection') {
      return featureIsEnabledForThisUser
        ? OLD_FEATURES_CONTENT_TYPES
        : CONTENT_TYPES
    }
    return CREATE_MENU_DEFAULT_OPTIONS(
      featureIsEnabledForThisUser,
      creatingFrom === 'Folder'
    )
  }, [featureIsEnabledForThisUser, creatingFrom, loading])

  const setCurrent = (value: CreateMenuItem[], index: number) => {
    onReady(false)
    setParentElements(currentElements)
    setIndex(index)
    delay(() => {
      onReady(true)
      _setCurrentElements(value)
    }, 250)
  }

  useEffect(() => {
    _setCurrentElements(createMenuItems)
  }, [parentElements, createMenuItems])

  useEffect(() => {
    setIsRoot(
      JSON.stringify(currentElements) === JSON.stringify(createMenuItems)
    )
  }, [creatingFrom, currentElements, createMenuItems])

  if (loading) {
    return null
  }

  return (
    <>
      <div className="my-6">
        <Title size="S">
          <ol role="list" className="flex items-center space-x-4">
            {!isRoot && (
              <li className="flex space-x-4 items-center">
                <button onClick={() => setCurrent(createMenuItems, 0)}>
                  <Title size="S">
                    <Trans>Crea</Trans>
                  </Title>
                </button>
                <FontAwesomeIcon
                  className="h-3 w-3"
                  icon={['fas', 'chevron-right']}
                />
              </li>
            )}
            {!isRoot && parentElements.length === 7 && (
              <li className="flex space-x-4 items-center">
                <button onClick={() => setCurrent(parentElements, 0)}>
                  <Title size="S">
                    <Trans>Contenuto</Trans>
                  </Title>
                </button>
                <FontAwesomeIcon
                  className="h-3 w-3"
                  icon={['fas', 'chevron-right']}
                />
              </li>
            )}

            <li>
              {!isRoot ? (
                index < 4 ? (
                  translateWhenIsId(createMenuItems[index].title)
                ) : (
                  translateWhenIsId(parentElements[index].title)
                )
              ) : (
                <Trans>Crea</Trans>
              )}
            </li>
          </ol>
        </Title>
      </div>
      <div className="flex flex-wrap gap-4 sm:gap-12">
        {map(currentElements, (subprops: CreateMenuItem, index: number) => (
          <Item
            {...subprops}
            title={translateWhenIsId(subprops.title)}
            description={translateWhenIsId(subprops.description)}
            link={subprops.link || ''}
            icon={subprops.icon || ''}
            items={subprops.items || []}
            callback={onClose}
            key={index}
            setCurrent={setCurrent}
            index={index}
            searchParams={createElementPageSearchParams}
          />
        ))}
      </div>
    </>
  )
}

export default MenuCreateElements
